import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Brand-safe placeholder used whenever a banner image is missing or fails to load.
function BannerFallback() {
  return (
    <div
      data-testid="banner-fallback"
      className="absolute inset-0 flex items-center justify-center text-center px-6"
      style={{ background: "linear-gradient(135deg, #F48E2E 0%, #F6B266 45%, #FDEBD3 100%)" }}
    >
      <div className="max-w-2xl">
        <div className="text-white/90 uppercase tracking-[0.3em] text-[10px] md:text-xs mb-3">CityShor Pop-Up Bazaar</div>
        <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-tight text-white drop-shadow-sm">
          India's Online Lifestyle Exhibition Platform
        </h1>
      </div>
    </div>
  );
}

export default function BannerCarousel({ banners = [] }) {
  const [i, setI] = useState(0);
  const [failed, setFailed] = useState({}); // { [banner_id]: true } when image fails to load
  // Warm the browser cache with every slide so later slides are not blank while they download.
  useEffect(() => {
    banners.forEach((b) => { if (b.image_base64) { const im = new Image(); im.src = b.image_base64; } });
  }, [banners]);
  useEffect(() => {
    if (!banners.length) return;
    const id = setInterval(() => setI((v) => (v + 1) % banners.length), 5500);
    return () => clearInterval(id);
  }, [banners.length]);

  // If there is nothing to show at all, still render a branded hero so the page
  // never has an ugly gap. Admin can add banners from the Banners tab.
  if (!banners.length) {
    return (
      <section data-testid="hero-banner" className="relative w-full">
        <div className="relative overflow-hidden bg-gray-100 aspect-[8/3] md:rounded-3xl md:mx-4 lg:mx-8 md:mt-8">
          <BannerFallback />
        </div>
      </section>
    );
  }
  const b = banners[i];
  const showFallback = !b.image_base64 || failed[b.id];

  return (
    <section data-testid="hero-banner" className="relative w-full">
      {/*
        Responsive banner sizing (no fixed pixel heights).
        A single aspect-[8/3] ratio (matching the recommended 2400x900 upload)
        is used at every breakpoint so the banner fills the available width with
        no empty letterboxing and no cropping, on both mobile and desktop.
        Master upload size recommendation for /app/backend Admin > Banners:
          2400 × 900 px, JPEG/WebP under 500 KB. Since the container ratio now
          matches this exactly, the full image is always visible — no safe zone
          needed.
      */}
      <div className="relative overflow-hidden bg-gray-100 aspect-[8/3] md:rounded-3xl md:mx-4 lg:mx-8 md:mt-8">
        <AnimatePresence mode="wait">
          <motion.div key={i}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6 }}
            className="absolute inset-0">
            {showFallback ? (
              <BannerFallback />
            ) : (
              <img
                src={b.image_base64}
                alt={b.title || "CityShor Pop-Up Bazaar"}
                loading="eager"
                fetchpriority="high"
                onError={() => setFailed((f) => ({ ...f, [b.id]: true }))}
                className="w-full h-full object-contain sm:object-cover object-center"
              />
            )}
            {!showFallback && b.show_overlay && (
              <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/70 via-black/30 to-transparent md:via-black/20" />
            )}
            {!showFallback && b.show_overlay && (
              <div className="absolute inset-0 flex items-end md:items-center px-6 md:px-12 lg:px-16 pb-8 md:pb-0">
                <div className="max-w-xl text-white">
                  <div className="text-[10px] md:text-xs uppercase tracking-[0.3em] mb-2 md:mb-3 text-white/90">CityShor Pop-Up Bazaar</div>
                  <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-none tracking-tight mb-3 md:mb-4">{b.title}</h1>
                  <p className="text-sm md:text-base lg:text-lg text-white/90 mb-4 md:mb-6 line-clamp-2 md:line-clamp-none">{b.subtitle}</p>
                  <Link to={b.cta_link || "/"}>
                    <Button data-testid={`banner-cta-${i}`} className="rounded-full bg-white text-[#111827] hover:bg-gray-100 h-10 md:h-11 px-5 md:px-6 text-xs md:text-sm font-medium">
                      {b.cta_text || "Shop Now"}
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {banners.length > 1 && (
          <>
            <button data-testid="banner-prev" onClick={() => setI((v) => (v - 1 + banners.length) % banners.length)} className="hidden md:block absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="banner-next" onClick={() => setI((v) => (v + 1) % banners.length)} className="hidden md:block absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white">
              <ChevronRight className="h-4 w-4" />
            </button>
            <div className="absolute bottom-3 md:bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {banners.map((_, idx) => (
                <button key={idx} onClick={() => setI(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all ${idx === i ? "bg-white w-8" : "bg-white/50 w-3"}`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
